/**
 * @typedef {import('../../types/AuthContext').default} Auth
 * @typedef {import('../Agent/GetContact').default} Contact
 * @typedef {import('../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]} ContactProperty
 * @typedef {import('../../shared/SettingProvider/Context').default} Settings
 * @typedef {{ amountToLoan: number | null, amortizationYears: number | null, escrowInsuranceAmount: number | null, escrowInsuranceFrequency: 'annual' | 'monthly', escrowInsuranceMonthly: number | null, escrowTaxAmount: number | null, escrowTaxFrequency: 'annual' | 'monthly', escrowTaxMonthly: number | null, originalDateOfContract: Date | null, rate: number | null }} MortgageDetails
 */

import {
	getAgentId,
	getContact,
	getContactHomeownerProperties,
} from '../Agent/agent-service';
import { AuthContext } from '../../shared/AuthProvider';
import { translate } from '../Internationalization';
import { withSettings } from '../../shared/SettingProvider/SettingProvider';
import captureError from '../../utils/captureError';
import ContactFinancesAndValuation from './ContactFinancesAndValuation';
import flags from '../../shared/Flags';
import Loading from '../../shared/Loading';
import paths from '../../config/local/paths';
import React from 'react';

/**
 * @typedef {{
 * 	accessRole?: 'agent' | 'manager',
 * 	match: {
 * 		params: {
 * 			contactId: string,
 * 			propertyId: string
 * 		}
 * 	},
 * 	history: {
 * 		goBack: () => void
 * 	},
 * 	settings: Settings
 * }} Props
 */

/**
 * @typedef {{
 * 	agentId: string,
 * 	contact: Contact | null,
 * 	errorMessage: string | null,
 * 	isGettingData: boolean,
 * 	properties: ContactProperty[],
 * 	selectedPropertyId: string
 * }} State
 */

/** @extends {React.Component<Props, State>} */
class ContactPropertyFinances extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/** @param {Props} props */
	constructor(props) {
		super(props);

		this.state = {
			agentId: '',
			contact: null,
			errorMessage: null,
			isGettingData: true,
			properties: [],
			selectedPropertyId: props.match.params.propertyId,
		};

		this.handleMortgageSaved = this.#handleMortgageSaved.bind(this);
		this.handleNavigateBack = this.#handleNavigateBack.bind(this);
		this.handlePropertySelect = this.#handlePropertySelect.bind(this);
	}

	/** @returns {Promise<void>} */
	async componentDidMount() {
		try {
			const contact = await getContact(
				this.props.match.params.contactId,
				this.context.accessToken
			);
			const [agentId, properties] = await Promise.all([
				this.#getAgentId(contact),
				contact.homeownerId
					? getContactHomeownerProperties(
							contact.homeownerId,
							this.context.accessToken
					  )
					: [],
			]);
			const selectedProperty = properties.find(
				(property) => property.id === this.props.match.params.propertyId
			);

			this.setState({
				agentId,
				contact,
				isGettingData: false,
				properties,
				selectedPropertyId:
					selectedProperty?.id ?? properties[0]?.id ?? '',
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {Contact} contact
	 * @returns {Promise<string>}
	 */
	async #getAgentId(contact) {
		if (this.#getAccessRole() === 'manager') {
			return contact.agentId ?? '';
		}

		return getAgentId(this.context.accessToken);
	}

	/** @returns {'agent' | 'manager'} */
	#getAccessRole() {
		if (this.props.accessRole) {
			return this.props.accessRole;
		}

		return this.context.roles.includes('manager') ? 'manager' : 'agent';
	}

	/** @returns {'agent_tier' | 'manager_tier'} */
	#getUserTier() {
		return this.#getAccessRole() === 'manager'
			? 'manager_tier'
			: 'agent_tier';
	}

	/**
	 * @param {string} flag
	 * @returns {boolean}
	 */
	#isSubscriptionFlagEnabled(flag) {
		return this.props.settings.isEnabled(flag, this.#getUserTier());
	}

	/**
	 * @returns {{ contactFinanceWidgets: string, mortgageSection: string, propertyValuationHkAvm: string, propertyValuationQuantariumAvm: string }}
	 */
	#getSubscriptionFlags() {
		if (this.#getAccessRole() === 'manager') {
			return {
				contactFinanceWidgets: flags.manager.contactFinanceWidgets,
				mortgageSection: flags.manager.mortgageSection,
				propertyValuationHkAvm: flags.manager.propertyValuationHkAvm,
				propertyValuationQuantariumAvm:
					flags.manager.propertyValuationQuantariumAvm,
			};
		}

		return {
			contactFinanceWidgets: flags.agent.contactFinanceWidgets,
			mortgageSection: flags.agent.mortgageSection,
			propertyValuationHkAvm: flags.agent.propertyValuationHkAvm,
			propertyValuationQuantariumAvm:
				flags.agent.propertyValuationQuantariumAvm,
		};
	}

	/**
	 * @returns {number}
	 */
	#getSubscriptionTier() {
		return this.props.settings.getTierValue(this.#getUserTier());
	}

	/** @returns {string} */
	#getSubscriptionPath() {
		return this.#getAccessRole() === 'manager'
			? paths.app.admin.groups.root
			: paths.app.agent.subscription.root;
	}

	/**
	 * @param {string} propertyId
	 * @param {MortgageDetails} mortgageDetails
	 * @returns {void}
	 */
	#handleMortgageSaved(propertyId, mortgageDetails) {
		this.setState((prevState) => ({
			properties: prevState.properties.map((property) => {
				if (property.id !== propertyId) {
					return property;
				}

				return {
					...property,
					details: {
						...property.details,
						...mortgageDetails,
					},
				};
			}),
		}));
	}

	/** @returns {void} */
	#handleNavigateBack() {
		this.props.history.goBack();
	}

	/**
	 * @param {string} propertyId
	 * @returns {void}
	 */
	#handlePropertySelect(propertyId) {
		this.setState({
			selectedPropertyId: propertyId,
		});
	}

	/** @returns {JSX.Element | null} */
	render() {
		if (this.state.isGettingData) {
			return <Loading />;
		}

		if (this.state.errorMessage) {
			return (
				<p className="message message-themed-light">
					{this.state.errorMessage}
				</p>
			);
		}

		const contact = /** @type {Contact | null} */ (this.state.contact);

		if (!contact?.homeownerId) {
			return null;
		}

		const subscriptionFlags = this.#getSubscriptionFlags();

		return (
			<ContactFinancesAndValuation
				accessToken={this.context.accessToken}
				agentId={this.state.agentId}
				canViewFinanceWidgets={this.#isSubscriptionFlagEnabled(
					subscriptionFlags.contactFinanceWidgets
				)}
				canViewHkAvm={this.#isSubscriptionFlagEnabled(
					subscriptionFlags.propertyValuationHkAvm
				)}
				canViewMortgage={this.#isSubscriptionFlagEnabled(
					subscriptionFlags.mortgageSection
				)}
				canViewQuantariumAvm={this.#isSubscriptionFlagEnabled(
					subscriptionFlags.propertyValuationQuantariumAvm
				)}
				contactId={this.props.match.params.contactId}
				hidePropertySelector={true}
				homeownerId={contact.homeownerId}
				isGettingProperties={false}
				onBack={this.handleNavigateBack}
				onMortgageSaved={this.handleMortgageSaved}
				onPropertySelect={this.handlePropertySelect}
				properties={this.state.properties}
				selectedPropertyId={this.state.selectedPropertyId}
				subscriptionPath={this.#getSubscriptionPath()}
				subscriptionTier={this.#getSubscriptionTier()}
				userId={this.context.userId}
			/>
		);
	}
}

ContactPropertyFinances.contextType = AuthContext;

export default withSettings(ContactPropertyFinances);
